import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  createInitialMpesaActivationDetailsDraft,
  type MpesaActivationDetailsDraft,
  type MpesaActivationStatus,
} from '../../utils/mpesaActivation';

/**
 * Owner-side M-Pesa setup status, keyed by location (persisted).
 *
 * The status object is the server's truth: the screen renders from it and
 * every action refetches it. The details draft is the owner's half-filled
 * "Let BizPlus set it up" form, so a closed app does not lose it. The SMS code
 * is never stored here.
 */

export interface MpesaActivationLocationEntry {
  status: MpesaActivationStatus | null;
  fetchedAt: string | null;
  error: string | null;
}

export interface MpesaActivationState {
  byLocation: Record<string, MpesaActivationLocationEntry>;
  draftByLocation: Record<string, MpesaActivationDetailsDraft>;
}

const initialState: MpesaActivationState = {
  byLocation: {},
  draftByLocation: {},
};

const emptyEntry = (): MpesaActivationLocationEntry => ({
  status: null,
  fetchedAt: null,
  error: null,
});

const mpesaActivationSlice = createSlice({
  name: 'backofficeMpesaActivation',
  initialState,
  reducers: {
    setMpesaActivationStatus: (
      state,
      action: PayloadAction<{
        locationId: string;
        status: MpesaActivationStatus;
        fetchedAt: string;
      }>,
    ) => {
      const { locationId, status, fetchedAt } = action.payload;
      if (!locationId) return;
      state.byLocation[locationId] = { status, fetchedAt, error: null };
    },
    setMpesaActivationError: (
      state,
      action: PayloadAction<{ locationId: string; error: string }>,
    ) => {
      const { locationId, error } = action.payload;
      if (!locationId) return;
      const entry = state.byLocation[locationId] ?? emptyEntry();
      state.byLocation[locationId] = { ...entry, error };
    },
    clearMpesaActivationLocation: (state, action: PayloadAction<string>) => {
      delete state.byLocation[action.payload];
      delete state.draftByLocation[action.payload];
    },
    setMpesaActivationDraft: (
      state,
      action: PayloadAction<{
        locationId: string;
        draft: Partial<MpesaActivationDetailsDraft>;
      }>,
    ) => {
      const { locationId, draft } = action.payload;
      if (!locationId) return;
      state.draftByLocation[locationId] = {
        ...(state.draftByLocation[locationId] ??
          createInitialMpesaActivationDetailsDraft()),
        ...draft,
      };
    },
    resetMpesaActivationDraft: (state, action: PayloadAction<string>) => {
      delete state.draftByLocation[action.payload];
    },
  },
});

export const {
  setMpesaActivationStatus,
  setMpesaActivationError,
  clearMpesaActivationLocation,
  setMpesaActivationDraft,
  resetMpesaActivationDraft,
} = mpesaActivationSlice.actions;

export default mpesaActivationSlice.reducer;

// Selectors
import type { RootState } from '../types';

const EMPTY_ENTRY: MpesaActivationLocationEntry = Object.freeze(emptyEntry());
const EMPTY_DRAFT: MpesaActivationDetailsDraft = Object.freeze(
  createInitialMpesaActivationDetailsDraft(),
);

export const selectMpesaActivationEntry = (
  state: RootState,
  locationId: string,
): MpesaActivationLocationEntry =>
  (locationId && state.backofficeMpesaActivation.byLocation[locationId]) ||
  EMPTY_ENTRY;

export const selectMpesaActivationStatus = (
  state: RootState,
  locationId: string,
): MpesaActivationStatus | null =>
  selectMpesaActivationEntry(state, locationId).status;

export const selectMpesaActivationDraft = (
  state: RootState,
  locationId: string,
): MpesaActivationDetailsDraft =>
  (locationId && state.backofficeMpesaActivation.draftByLocation[locationId]) ||
  EMPTY_DRAFT;
